import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Modal from '@/components/base/Modal';
import Select from '@/components/base/Select';
import Textarea from '@/components/base/Textarea';
import TimeInput from '@/components/base/TimeInput';
import { cn } from '@/lib/cn';
import { systemSettings } from '@/mocks/settings';
import { hasProfileChanges } from '@/lib/laundryProfile';
import LocationPicker, { type MapPoint } from '@/components/feature/LocationPicker';
import type { LaundryCoverageOptions, LaundryProfile } from '@/types';
import CoverageDistrictsField from './CoverageDistrictsField';
import LogoUploadField from './LogoUploadField';

interface RequestModificationModalProps {
  open: boolean;
  profile: LaundryProfile;
  coverage: LaundryCoverageOptions;
  highlightDurations?: boolean;
  onClose: () => void;
  onSubmit: (proposed: LaundryProfile) => Promise<void>;
}

interface FormState {
  name: string;
  description: string;
  phone: string;
  district: string;
  street: string;
  building: string;
  coverageDistricts: string[];
  fromTime: string;
  toTime: string;
  normalHours: string;
  urgentHours: string;
  logoDataUrl: string | null;
}

const PHONE_PATTERN = /^(?:\+966|0)5\d{8}$/;

const initialState = (profile: LaundryProfile): FormState => ({
  name: profile.name,
  description: profile.description,
  phone: profile.phone,
  district: profile.address.district,
  street: profile.address.street,
  building: profile.address.building,
  coverageDistricts: [...profile.coverageDistricts],
  fromTime: profile.fromTime,
  toTime: profile.toTime,
  normalHours: profile.normalHours == null ? '' : String(profile.normalHours),
  urgentHours: profile.urgentHours == null ? '' : String(profile.urgentHours),
  logoDataUrl: null,
});

function SectionBlock({
  icon,
  title,
  children,
  className,
}: {
  icon: string;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border border-background-200 bg-background-50 p-4 shadow-xs', className)}>
      <div className="mb-3 flex items-center gap-2.5 border-b border-background-100 pb-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className={`${icon} text-base leading-none`} aria-hidden="true" />
        </span>
        <h4 className="text-sm font-semibold text-foreground-950">{title}</h4>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function RequestModificationModal({
  open,
  profile,
  coverage,
  highlightDurations = false,
  onClose,
  onSubmit,
}: RequestModificationModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => initialState(profile));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mapPoint, setMapPoint] = useState<MapPoint | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialState(profile));
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
      setMapPoint(null);
    }
  }, [open, profile]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const parseHours = (raw: string): number | null | 'invalid' => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value <= 0) return 'invalid';
    return value;
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('settings.form.error.name');
    if (!form.phone.trim()) next.phone = t('settings.form.error.phone');
    else if (!PHONE_PATTERN.test(form.phone.trim())) next.phone = t('settings.form.error.phoneFormat');

    const normal = parseHours(form.normalHours);
    if (normal === 'invalid') next.normalHours = t('settings.form.error.duration');
    const urgent = parseHours(form.urgentHours);
    if (urgent === 'invalid') next.urgentHours = t('settings.form.error.duration');

    setErrors(next);
    return Object.keys(next).length === 0 && normal !== 'invalid' && urgent !== 'invalid';
  };

  const buildProposed = (): LaundryProfile => {
    const normal = parseHours(form.normalHours);
    const urgent = parseHours(form.urgentHours);
    return {
      name: form.name.trim(),
      description: form.description.trim(),
      logoUrl: form.logoDataUrl ?? profile.logoUrl,
      phone: form.phone.trim(),
      address: {
        cityId: profile.address.cityId,
        district: form.district,
        street: form.street.trim(),
        building: form.building.trim(),
      },
      coverageDistricts: form.coverageDistricts,
      fromTime: form.fromTime,
      toTime: form.toTime,
      normalHours: normal === 'invalid' ? null : normal,
      urgentHours: urgent === 'invalid' ? null : urgent,
    };
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validate()) return;

    const proposed = buildProposed();
    if (!hasProfileChanges(profile, proposed)) {
      setSubmitError(t('settings.form.error.noChanges'));
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(proposed);
      onClose();
    } catch {
      setSubmitError(t('settings.form.error.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const previewAddress = {
    cityId: profile.address.cityId,
    district: form.district,
    street: form.street,
    building: form.building,
  };

  const previewLogo = form.logoDataUrl ?? profile.logoUrl;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={t('settings.form.title')}
      description={t('settings.form.desc')}
      size="lg"
      fullScreen
      footer={
        <>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={submitting}
          >
            {t('settings.form.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            icon="ri-send-plane-line"
            loading={submitting}
            onClick={handleSubmit}
          >
            {t('settings.form.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Basic data */}
        <SectionBlock icon="ri-store-2-line" title={t('settings.form.basic')}>
          <Input
            id="mod-name"
            name="name"
            label={t('settings.form.name')}
            requiredMark
            value={form.name}
            maxLength={120}
            error={errors.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <Textarea
            id="mod-description"
            name="description"
            label={t('settings.form.description')}
            value={form.description}
            maxLength={500}
            error={errors.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <LogoUploadField
            id="mod-logo"
            name="logo"
            preview={previewLogo}
            onChange={(dataUrl) => set('logoDataUrl', dataUrl)}
            accept={systemSettings.imageUpload.accept}
            maxSizeMB={systemSettings.imageUpload.maxSizeMB}
            hint={t('settings.image.hint')}
            labels={{
              choose: t('settings.image.choose'),
              replace: t('settings.image.replace'),
              remove: t('settings.image.remove'),
              none: t('settings.image.none'),
              tooLarge: t('settings.image.tooLarge'),
              wrongType: t('settings.image.wrongType'),
            }}
          />
          <Input
            id="mod-phone"
            name="phone"
            label={t('settings.form.phone')}
            requiredMark
            ltr
            inputMode="tel"
            value={form.phone}
            maxLength={14}
            error={errors.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </SectionBlock>

        {/* Address & coverage */}
        <SectionBlock icon="ri-map-pin-2-line" title={t('settings.form.addressCoverage')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="mod-city"
              name="city"
              label={t('settings.form.city')}
              value={coverage.cityName}
              readOnly
              disabled
            />
            <Select
              id="mod-district"
              name="district"
              label={t('settings.form.district')}
              placeholder={t('settings.form.district')}
              value={form.district}
              options={coverage.districts.map((district) => ({ value: district, label: district }))}
              onChange={(e) => set('district', e.target.value)}
            />
            <Input
              id="mod-street"
              name="street"
              label={t('settings.form.street')}
              value={form.street}
              maxLength={120}
              onChange={(e) => set('street', e.target.value)}
            />
            <Input
              id="mod-building"
              name="building"
              label={t('settings.form.building')}
              ltr
              value={form.building}
              maxLength={12}
              onChange={(e) => set('building', e.target.value)}
            />
          </div>
          <p className="text-xs text-foreground-500">{t('settings.form.addressHint')}</p>
          <LocationPicker value={mapPoint} onChange={setMapPoint} address={previewAddress} />
          <CoverageDistrictsField
            districts={coverage.districts}
            value={form.coverageDistricts}
            onChange={(value) => set('coverageDistricts', value)}
            label={t('settings.form.coverage')}
            hint={t('settings.form.coverageHint')}
          />
        </SectionBlock>

        {/* Working hours */}
        <SectionBlock icon="ri-time-line" title={t('settings.form.hours')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TimeInput
              id="mod-from"
              name="fromTime"
              label={t('settings.form.fromTime')}
              value={form.fromTime}
              onChange={(e) => set('fromTime', e.target.value)}
            />
            <TimeInput
              id="mod-to"
              name="toTime"
              label={t('settings.form.toTime')}
              value={form.toTime}
              onChange={(e) => set('toTime', e.target.value)}
            />
          </div>
        </SectionBlock>

        {/* Durations */}
        <SectionBlock
          icon="ri-timer-flash-line"
          title={t('settings.form.durations')}
          className={highlightDurations ? 'ring-2 ring-accent-300' : undefined}
        >
          {highlightDurations ? (
            <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-xs font-medium text-accent-900">
              <i className="ri-information-line text-base" aria-hidden="true" />
              {t('settings.form.highlightDurations')}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="mod-normal-hours"
              name="normalHours"
              type="number"
              min={1}
              ltr
              label={t('settings.form.normalHours')}
              value={form.normalHours}
              error={errors.normalHours}
              onChange={(e) => set('normalHours', e.target.value)}
            />
            <Input
              id="mod-urgent-hours"
              name="urgentHours"
              type="number"
              min={1}
              ltr
              label={t('settings.form.urgentHours')}
              value={form.urgentHours}
              error={errors.urgentHours}
              onChange={(e) => set('urgentHours', e.target.value)}
            />
          </div>
          <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
            <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('settings.form.durationHint')}
          </p>
        </SectionBlock>

        {submitError ? (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          >
            <i className="ri-error-warning-line mt-0.5 text-lg text-rose-600" aria-hidden="true" />
            {submitError}
          </div>
        ) : null}

        <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          {t('settings.form.note')}
        </p>
      </div>
    </Modal>
  );
}